import { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Package, Truck, KeyRound } from "lucide-react";

const CATS = ["TEXTBOOKS", "NOTEBOOKS", "UNIFORMS", "STATIONERY_KITS", "LAB_GEAR", "BAGS_AND_SHOES"];
const TABS = [
  { id: "products", label: "Products" },
  { id: "orders", label: "Orders & Delivery" },
  { id: "profile", label: "Store Profile" },
];

function Products() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const blank = { title: "", category_code: "TEXTBOOKS", item_code: "", grade_tag: "", subject_tag: "", mrp: 0, selling_price: 0, stock_qty: 0, low_stock_threshold: 5, image_url: "", promo_code: "", discount_pct: 0 };
  const [f, setF] = useState(blank);
  const load = () => api.get("/store/vendor/products").then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const create = async () => {
    if (!f.title || !f.selling_price) { toast.error("Title and selling price required"); return; }
    try {
      await api.post("/store/vendor/products", { ...f, mrp: parseFloat(f.mrp), selling_price: parseFloat(f.selling_price), stock_qty: parseInt(f.stock_qty || 0), low_stock_threshold: parseInt(f.low_stock_threshold || 5), discount_pct: parseFloat(f.discount_pct || 0) });
      toast.success("SKU added"); setOpen(false); setF(blank); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { await api.delete(`/store/vendor/products/${id}`); load(); };
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center"><h2 className="display text-2xl font-bold">Inventory</h2>
        <button className="nb-btn" onClick={() => setOpen(true)} data-testid="vendor-add-sku"><Plus size={16} /> Add SKU</button></div>
      <div className="nb-card p-0 overflow-hidden">
        <table className="nb-table">
          <thead><tr><th>Title</th><th>Category</th><th>Grade</th><th>MRP</th><th>Price</th><th>Stock</th><th></th></tr></thead>
          <tbody>
            {rows.map(p => (
              <tr key={p.product_id} data-testid={`vendor-sku-${p.product_id}`}>
                <td className="font-bold">{p.title}</td><td className="mono text-xs">{p.category_code}</td>
                <td>{p.grade_tag}</td><td className="mono">₹{p.mrp}</td><td className="mono font-bold">₹{p.selling_price}</td>
                <td className={p.stock_qty <= p.low_stock_threshold ? "text-red-600 font-bold" : ""}>{p.stock_qty}{p.stock_qty <= p.low_stock_threshold && " ⚠"}</td>
                <td><button className="text-red-600 text-xs" onClick={() => del(p.product_id)}>Delete</button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-slate-500">No products yet</td></tr>}
          </tbody>
        </table>
      </div>
      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => setOpen(false)}>
          <div className="nb-card bg-white p-5 max-w-lg w-full space-y-3" onClick={e => e.stopPropagation()}>
            <h3 className="display text-lg font-bold">Add SKU</h3>
            <input className="nb-input" placeholder="Title *" value={f.title} onChange={set("title")} data-testid="sku-title" />
            <div className="grid grid-cols-2 gap-2">
              <select className="nb-select" value={f.category_code} onChange={set("category_code")} data-testid="sku-category">
                {CATS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <input className="nb-input mono" placeholder="ISBN / Item Code" value={f.item_code} onChange={set("item_code")} data-testid="sku-code" />
              <input className="nb-input" placeholder="Grade tag" value={f.grade_tag} onChange={set("grade_tag")} data-testid="sku-grade" />
              <input className="nb-input" placeholder="Subject tag" value={f.subject_tag} onChange={set("subject_tag")} />
              <input type="number" className="nb-input mono" placeholder="MRP" value={f.mrp} onChange={set("mrp")} data-testid="sku-mrp" />
              <input type="number" className="nb-input mono" placeholder="Selling Price *" value={f.selling_price} onChange={set("selling_price")} data-testid="sku-price" />
              <input type="number" className="nb-input mono" placeholder="Stock Qty" value={f.stock_qty} onChange={set("stock_qty")} data-testid="sku-stock" />
              <input type="number" className="nb-input mono" placeholder="Low-stock alert" value={f.low_stock_threshold} onChange={set("low_stock_threshold")} />
              <input className="nb-input mono" placeholder="Promo code" value={f.promo_code} onChange={set("promo_code")} />
              <input type="number" className="nb-input mono" placeholder="Discount %" value={f.discount_pct} onChange={set("discount_pct")} />
            </div>
            <input className="nb-input" placeholder="Image URL (optional)" value={f.image_url} onChange={set("image_url")} />
            <div className="flex justify-end gap-2"><button className="nb-btn nb-btn-ghost text-xs" onClick={() => setOpen(false)}>Cancel</button>
              <button className="nb-btn text-xs" onClick={create} data-testid="sku-save">Save SKU</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

function Orders() {
  const [rows, setRows] = useState([]);
  const [otp, setOtp] = useState({});
  const load = () => api.get("/store/vendor/orders").then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const verify = async (id) => {
    try {
      const r = await api.post(`/store/vendor/orders/${id}/verify-otp`, { delivery_otp: otp[id] || "" });
      toast.success(r.data.idempotent ? "Already delivered" : "Delivered! 7-day return window started"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-4">
      <h2 className="display text-2xl font-bold flex items-center gap-2"><Truck size={22} /> Orders</h2>
      <div className="nb-card p-0 overflow-hidden">
        <table className="nb-table">
          <thead><tr><th>Order</th><th>Customer</th><th>Items</th><th>Total</th><th>Status</th><th>Handover (OTP)</th></tr></thead>
          <tbody>
            {rows.map(o => (
              <tr key={o.order_id} data-testid={`vendor-order-${o.order_id}`}>
                <td className="mono text-xs">{o.order_id.slice(0, 8)}</td>
                <td>{o.customer_name}<div className="text-xs opacity-60 mono">{o.customer_phone}</div></td>
                <td className="text-xs">{o.line_items?.map(l => `${l.title}×${l.qty}`).join(", ")}</td>
                <td className="mono font-bold">₹{o.total}</td>
                <td><span className={`nb-badge ${o.status === "DELIVERED" ? "bg-[#86EFAC]" : "bg-[#FDE047]"}`}>{o.status}</span></td>
                <td>{o.status === "DELIVERED"
                  ? <span className="text-xs text-slate-400">✓ {o.delivered_at?.slice(0, 10)}</span>
                  : <div className="flex gap-1">
                      <input className="nb-input mono w-20 text-sm" placeholder="4-digit" value={otp[o.order_id] || ""} onChange={e => setOtp({ ...otp, [o.order_id]: e.target.value })} data-testid={`vendor-otp-input-${o.order_id}`} />
                      <button className="nb-btn text-xs" onClick={() => verify(o.order_id)} data-testid={`vendor-otp-verify-${o.order_id}`}><KeyRound size={12} /></button>
                    </div>}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-slate-500">No orders yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Profile() {
  const [v, setV] = useState(null);
  useEffect(() => { api.get("/store/vendor/me").then(r => setV(r.data)).catch(() => {}); }, []);
  const save = async () => {
    try {
      await api.patch("/store/vendor/me", {
        fulfillment_pickup: v.fulfillment_pickup, fulfillment_delivery: v.fulfillment_delivery,
        delivery_radius_km: parseFloat(v.delivery_radius_km), delivery_fee: parseFloat(v.delivery_fee),
        service_radius_km: parseFloat(v.service_radius_km),
      });
      toast.success("Profile updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (!v) return <div className="mono">Loading…</div>;
  return (
    <div className="nb-card p-6 max-w-lg space-y-3" data-testid="vendor-profile">
      <h2 className="display text-xl font-bold flex items-center gap-2"><Package size={20} /> {v.business_name}</h2>
      <div className="text-sm">KYC: <span className={`nb-badge ${v.kyc_status === "VERIFIED" ? "bg-[#86EFAC]" : "bg-[#FDE047]"}`}>{v.kyc_status}</span> · {v.city} {v.postal_code}</div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!v.fulfillment_pickup} onChange={e => setV({ ...v, fulfillment_pickup: e.target.checked })} data-testid="vendor-pickup" /> Store Pickup</label>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!v.fulfillment_delivery} onChange={e => setV({ ...v, fulfillment_delivery: e.target.checked })} data-testid="vendor-delivery" /> Door Delivery</label>
      <div className="grid grid-cols-2 gap-2">
        <input type="number" className="nb-input mono" placeholder="Delivery radius km" value={v.delivery_radius_km} onChange={e => setV({ ...v, delivery_radius_km: e.target.value })} />
        <input type="number" className="nb-input mono" placeholder="Delivery fee ₹" value={v.delivery_fee} onChange={e => setV({ ...v, delivery_fee: e.target.value })} data-testid="vendor-delivery-fee" />
      </div>
      <button className="nb-btn" onClick={save} data-testid="vendor-profile-save">Save</button>
    </div>
  );
}

export default function VendorDashboard() {
  const [tab, setTab] = useState("products");
  return (
    <Shell title="Store Vendor" subtitle="Schoolz Store console" tabs={TABS} active={tab} onTab={setTab}>
      {tab === "products" && <Products />}
      {tab === "orders" && <Orders />}
      {tab === "profile" && <Profile />}
    </Shell>
  );
}
